<script setup lang="ts">
import { Heart, X } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Product } from '@/types/products';

import EcoFriendlyIcon from '@/components/labels/EcoFriendlyIcon.vue';
import { useFavorites } from '@/composables/useFavorites';
import { useProductLink } from '@/composables/useProductLink';
import { vGtmTrack } from '@/directives/v-gtm-track.js';
import { convertUrlToResizer, formatPrice, isEcoFriendly } from '@/utils/utils.js';

import TheStarRating from '../mode-list/TheStarRating.vue';

const props = withDefaults(
  defineProps<{
    product: Product;
    showFavorite?: boolean;
    prefixUrl: string | null;
    handleClose: (event: PointerEvent) => void;
  }>(),
  {
    showFavorite: true,
  },
);

const { t } = useI18n();
const { handleAddCrossSellingToFavorite } = useFavorites();

const isFavorite = ref<boolean>(false);
const imageSrc = ref<string>(props.product.image ?? 'https://empowher.org/wp-content/uploads/2021/03/image-placeholder-350x350-1-300x300.png');
const rating = computed(() => props.product.properties.find(it => it.propertyName === 'rating')?.value?.rating ?? null);
const displayedRating = ref((rating.value! / 2).toFixed(1));
const ratingReviewCount = computed(() => props.product.properties.find(it => it.propertyName === 'rating')?.value?.ratingReviewCount ?? null);
const priceFrom = formatPrice(props.product.priceFrom, props.product.currency);
const originalPrice = formatPrice(props.product.originalPrice, props.product.currency);
const hasDiscount = computed(() => (originalPrice > priceFrom) && props.product.originalPrice !== null);

const { productLink } = useProductLink(() => props.product, () => props.prefixUrl);
const externalLink = computed(() => productLink.value ?? '');

function handleImgError() {
  imageSrc.value = 'https://empowher.org/wp-content/uploads/2021/03/image-placeholder-350x350-1-300x300.png';
}

function handleFavorite(event: Event) {
  handleAddCrossSellingToFavorite(event, isFavorite);
}
</script>

<template>
  <a
    v-gtm-track:card="{ product: props.product }" :href="externalLink" class="yb-preset popup-card" target="_blank"
    :aria-label="t('card.label')"
  >
    <picture>
      <div v-if="isEcoFriendly(product)" class="eco-friendly-badge">
        <EcoFriendlyIcon />
      </div>
      <img :src="convertUrlToResizer(imageSrc)" alt="Lieu de l'activité" loading="lazy" @error="handleImgError">
      <div class="card-controls">
        <button
          v-if="showFavorite" id="card_favorite" type="button" :data-id="props.product.id" class="control"
          :aria-label="t('card.favorite')" @click="handleFavorite" @keydown.enter="handleFavorite"
        >
          <Heart :class="{ checked: isFavorite }" aria-hidden="true" />
        </button>
        <button class="control" @click="handleClose">
          <X />
        </button>
      </div>
    </picture>
    <section class="infos_wrapper">
      <div v-if="rating" class="rating">
        <TheStarRating :rating="displayedRating" />
        <p v-if="rating" class="small_text">
          {{ displayedRating }} / 5<span v-if="ratingReviewCount" class="review-count">({{ ratingReviewCount }})</span>
        </p>
      </div>
      <div class="title_text_clamp">
        {{ props.product.name ?? 'No name found.' }}
      </div>
      <p class="price_tag">
        {{ t('card.from') }}
        <span :style="[hasDiscount ? 'text-decoration: line-through; font-weight: var(--yb-text-light)' : '']">
          {{ originalPrice }}
        </span>&nbsp;
        <span v-if="hasDiscount" style="color: var(--yb-color-primary)">{{ priceFrom }}</span>
      </p>
    </section>
  </a>
</template>

<style lang="css">
@import '@/assets/globals.css';

.popup-card {
  all: unset;
  display: flex;
  width: 339px;
  height: 349px;
  margin: -1px;
  flex-direction: column;
  border-radius: var(--yb-card-radius);
  background-color: var(--yb-color-white);
  cursor: pointer;
  overflow: hidden;

  &:hover {
    .title_text_clamp {
      color: var(--yb-color-primary);
    }
  }

  picture {
    position: relative;
    width: 100%;
    height: 75%;
    aspect-ratio: var(--img-ratio);
    overflow: clip;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      aspect-ratio: var(--img-ratio);
      object-fit: cover;
      object-position: center;
    }

    .eco-friendly-badge {
      position: absolute;
      display: flex;
      background-color: var(--yb-color-ecofriendly);
      padding: 0.5rem;
      top: 0;
      left: 0.5rem;
      border-radius: 0 0 var(--yb-radius-sm) var(--yb-radius-sm);
      z-index: 5;
    }

    .card-controls {
      position: absolute;
      display: flex;
      gap: var(--yb-spacing-xs);
      top: 10px;
      right: 10px;
      padding: 0;
    }

    .control {
      display: flex;
      align-items: center;
      background-color: var(--yb-color-white);
      border: none;
      border-radius: var(--yb-radius-full);
      z-index: 5;
      transition: scale 0.3s ease-out;
      padding: var(--yb-spacing-xs);

      &:focus-visible {
        outline: 2px solid var(--yb-outline);
      }

      svg {
        stroke: var(--yb-color-black);
        stroke-width: 2px;
        cursor: pointer;
      }

      #card_favorite,
      svg.checked {
        stroke: var(--yb-color-primary);
        stroke-width: 2px;
        fill: var(--yb-color-primary);
      }

      @media (prefers-reduced-motion: no-preference) {
        &:hover {
          scale: 1.1;
        }
      }
    }
  }

  .infos_wrapper {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    justify-content: space-between;
    padding: var(--yb-spacing-sm);
    min-height: 25%;

    .rating {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      align-items: center;
      gap: var(--yb-spacing-xs);
      color: var(--yb-color-black);
      font-size: var(--yb-text-xs);

      p {
        font-weight: var(--yb-text-bold) !important;
      }

      .review-count {
        margin-left: 4px;
        font-weight: var(--yb-text-light);
      }
    }

    .title_text_clamp {
      overflow: hidden;
      display: -webkit-box;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      font-size: var(--yb-text-base);
      font-weight: var(--yb-text-semi-bold);
      color: var(--yb-card-title-color);
      line-height: var(--yb-text-lineheight);
      transition: color 0.3s ease-out;

      @media screen and (width < 768px) {
        font-size: var(--yb-text-base);
      }
    }
  }
}
</style>
